import { useEffect, useRef, useState } from 'react';
import { BarChart3, AlertTriangle } from 'lucide-react';

/**
 * Renderiza una spec Vega-Lite usando vega-embed.
 *
 * El import de vega-embed es lazy/dinámico para no inflar el bundle si la
 * página no muestra gráficos. Si el cliente nunca pregunta nada graficable,
 * vega no se descarga.
 */
export function ChartRenderer({ spec }: { spec: Record<string, unknown> }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let viewInstance: { finalize: () => void } | null = null;

    (async () => {
      try {
        const { default: embed } = await import('vega-embed');
        if (cancelled || !containerRef.current) return;

        const result = await embed(containerRef.current, spec as any, {
          actions: false,    // sin menú "..." (Export, View Source, etc.)
          renderer: 'svg',   // SVG escala mejor que canvas a alta densidad
          theme: undefined,
        });
        viewInstance = result.view;
        setReady(true);
      } catch (e: any) {
        if (!cancelled) setError(e?.message ?? 'No se pudo renderizar el gráfico.');
      }
    })();

    return () => {
      cancelled = true;
      try {
        viewInstance?.finalize();
      } catch {
        /* noop */
      }
    };
  }, [spec]);

  if (error) {
    return (
      <div className="mt-3 flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
        <AlertTriangle size={14} />
        <span>No se pudo renderizar el gráfico: {error}</span>
      </div>
    );
  }

  return (
    <div className="mt-3 rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
      <div className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-slate-500">
        <BarChart3 size={12} className="text-emerald-600" />
        Visualización
      </div>
      <div
        ref={containerRef}
        className={`w-full overflow-x-auto ${ready ? '' : 'min-h-[200px] animate-pulse rounded-lg bg-slate-50'}`}
      />
    </div>
  );
}
